import {
  format as formatDate,
  isValid,
  parse,
  parseISO,
  set,
  setHours,
  startOfDay,
  toDate,
} from "date-fns";
import { format, formatInTimeZone, fromZonedTime } from "date-fns-tz";

import { toFormattingTimezone } from "./legacy-timezone";

/*
 * Get the local date key part of an ISO8601 datetime string.
 *
 * The local date key is the date part of the ISO8601 datetime string,
 * in the local timezone of the datetime.
 *
 * @param isoDatetime The ISO8601 datetime string. Example: "2024-01-01T00:00:00-03:00".
 * @returns The local date key part of the ISO8601 datetime string. Example: "2024-01-01".
 */
export function localDateStr(isoDatetime: string): string {
  // Slicing the string to get only the date part.
  // This effectively gets the local date part of the ISO datetime string,
  // relative to whatever timezone that datetime is in.
  return isoDatetime.slice(0, 10);
}

function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function baseDateFormat(isoDatetime: string | undefined, formatStr: string): string {
  if (!isoDatetime) return "";

  const parsedDate = parseISO(isoDatetime);
  if (!isValid(parsedDate)) return "";

  return format(parsedDate, formatStr);
}

/** date-fns pattern for the US numeric date (e.g. 07/08/2026). */
export const US_DATE_FORMAT = "MM/dd/yyyy";

/**
 * date-fns pattern for the US numeric date with 24-hour time and zone abbreviation
 * (e.g. 07/08/2026 15:04 EDT).
 *
 * The zone label lets a reader in another timezone convert the time themselves, so use this
 * whenever the time is rendered in a timezone the reader may not share. This is the shape
 * {@link usDateTimeFormat} has always produced; it is named here so date/time/zone displays share
 * one definition rather than repeating the pattern.
 */
export const US_DATE_TIME_WITH_ZONE_FORMAT = `${US_DATE_FORMAT} HH:mm zzz`;

/**
 * Reports whether a FHIR `dateTime` string carries a time component.
 *
 * FHIR `dateTime` also permits date-only precision (`YYYY`, `YYYY-MM`, `YYYY-MM-DD`), for which
 * rendering a time would invent one. Callers use this to pick a date-only format instead.
 *
 * @param isoDateTime FHIR dateTime string.
 * @returns True when the value specifies a time of day.
 */
export function hasTimeComponent(isoDateTime: string): boolean {
  return isoDateTime.includes("T");
}

/* Date in MM-DD-YYYY format */
export function usDateFormatDashed(isoDatetime: string | undefined): string {
  return baseDateFormat(isoDatetime, "MM-dd-yyyy");
}

/* Date in MM/DD/YYYY format */
export function usDateFormat(isoDatetime: string | undefined): string {
  return baseDateFormat(isoDatetime, US_DATE_FORMAT);
}

/** Matches a FHIR `dateTime` carrying year-only precision (e.g. 2026). */
const YEAR_ONLY_PATTERN = /^\d{4}$/;

/** Matches a FHIR `dateTime` carrying year-month precision with a real month (e.g. 2026-07). */
const YEAR_MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;

/**
 * Formats a date-only FHIR `dateTime` at the precision it was given.
 *
 * FHIR permits `YYYY` and `YYYY-MM` alongside `YYYY-MM-DD`. Parsing those into a Date fills in the
 * missing parts, so `2026` would display as January 1st and `2026-07` as the 1st of July. A
 * reduced-precision value is therefore rendered from its own components rather than widened into a
 * calendar date it does not claim.
 *
 * An out-of-range month (`2026-13`) is not year-month precision, so it falls through to the
 * full-date path and comes back empty, the same as any other unparseable value.
 *
 * @param isoDate FHIR dateTime string with no time component.
 * @returns "yyyy", "MM/yyyy", or "MM/dd/yyyy" to match the precision of the input, or empty string
 *   when the value cannot be read as a date.
 */
export function formatDateAtGivenPrecision(isoDate: string): string {
  if (YEAR_ONLY_PATTERN.test(isoDate)) return isoDate;

  const yearMonth = YEAR_MONTH_PATTERN.exec(isoDate);
  if (yearMonth) {
    const [, year, month] = yearMonth;
    return `${month}/${year}`;
  }

  return usDateFormat(isoDate);
}

/**
 * Formats an ISO date-time as a calendar date in a specific IANA timezone.
 *
 * @param isoDateTime ISO 8601 instant (or undefined).
 * @param timezoneCode IANA timezone identifier (e.g. America/Los_Angeles).
 * @param formatStr date-fns format pattern; defaults to {@link US_DATE_FORMAT}.
 * @returns Formatted date string, or empty string when input is missing/invalid.
 */
export function formatDateInTimezone(
  isoDateTime: string | undefined,
  timezoneCode: string,
  formatStr: string = US_DATE_FORMAT,
): string {
  if (!isoDateTime || !timezoneCode) return "";

  const parsedDate = parseISO(isoDateTime);
  if (!isValid(parsedDate)) return "";

  return formatInTimeZone(parsedDate, toFormattingTimezone(timezoneCode), formatStr);
}

/** date-fns pattern for a short date-time with zone abbreviation (e.g. Jan 20, 2026 · 1:00 PM (PST)). */
export const SHORT_DATE_TIME_WITH_ZONE_FORMAT = "MMM d, yyyy · h:mm aa (zzz)";

/**
 * Formats an ISO instant as a short date-time with the zone abbreviation, in a given IANA timezone
 * (e.g. Jan 20, 2026 · 1:00 PM (PST)).
 *
 * @param isoDateTime ISO 8601 instant (or undefined).
 * @param timezoneCode IANA timezone identifier (e.g. America/Los_Angeles).
 * @returns Formatted date-time string, or empty string when input is missing/invalid.
 */
export function shortUSDateTimeInTimezone(
  isoDateTime: string | undefined,
  timezoneCode: string,
): string {
  return formatDateInTimezone(isoDateTime, timezoneCode, SHORT_DATE_TIME_WITH_ZONE_FORMAT);
}

/** date-fns pattern for weekday + zero-padded long date (e.g. Tuesday, July 08, 2026). */
export const LONG_DATE_WITH_WEEKDAY_FORMAT = "EEEE, MMMM dd, yyyy";

/** date-fns pattern for weekday + zero-padded long date with time (e.g. Tuesday, July 08, 2026 at 3:00 PM). */
export const LONG_DATE_WITH_WEEKDAY_AND_TIME_FORMAT = "EEEE, MMMM dd, yyyy 'at' h:mm a";

/**
 * date-fns pattern for weekday + zero-padded long date with time and zone abbreviation
 * (e.g. Tuesday, July 08, 2026 at 3:00pm EDT).
 *
 * The zone label lets a recipient in another timezone convert the time themselves, so use this
 * whenever the reader may not share the timezone the time is rendered in.
 */
export const LONG_DATE_WITH_WEEKDAY_AND_TIME_WITH_ZONE_FORMAT =
  "EEEE, MMMM dd, yyyy 'at' h:mmaaa zzz";

/* Date in "Month day, year" format (e.g. October 18, 2024) */
export function longDateFormat(isoDatetime: string | undefined): string {
  return baseDateFormat(isoDatetime, "MMMM d, yyyy");
}

/** Formats a Date as weekday + zero-padded long date (e.g. Tuesday, July 08, 2026). */
export function longDateWithWeekdayFormat(date: Date): string {
  return formatDate(date, LONG_DATE_WITH_WEEKDAY_FORMAT);
}

export function longDateFormatWithTime(isoDatetime: string | undefined): string {
  if (!isoDatetime) return "";
  return `${baseDateFormat(isoDatetime, "MMMM d, yyyy")} at ${baseDateFormat(isoDatetime, "h:mm a")}`;
}

/* Date and time in MM/DD/YYY 00:00 zzz format with user's timezone */
export function usDateTimeFormat(isoDatetime: string | undefined): string {
  if (!isoDatetime) {
    return "";
  }
  const userTimezone = getUserTimezone();
  return formatInTimeZone(parseISO(isoDatetime), userTimezone, US_DATE_TIME_WITH_ZONE_FORMAT);
}

/**
 * date-fns pattern for 24-hour time only (e.g. 14:05), for use with {@link formatDateInTimezone}
 * when only the time-of-day is needed, with no date and no zone abbreviation.
 */
export const TIME_FORMAT = "HH:mm";

/* Date as "December 7, 2024" */
export function longUSDateFormat(isoDatetime: string): string {
  if (!isoDatetime) {
    return "";
  }
  const userTimezone = getUserTimezone();
  return formatInTimeZone(parseISO(isoDatetime), userTimezone, "MMMM d, yyyy");
}

/* Date and time as "December 7, 2024, 3:00 PM" */
export function longUSDateTimeFormat(isoDatetime: string | undefined): string {
  if (!isoDatetime) {
    return "";
  }
  const userTimezone = getUserTimezone();
  return formatInTimeZone(parseISO(isoDatetime), userTimezone, "MMMM d, yyyy h:mm a");
}

/* Date and time as "December 7, 2024 · 3:00 PM (PST)" */
export function longUSDateTimeFormatWithTimezone(isoDatetime: string | undefined): string {
  if (!isoDatetime) {
    return "";
  }
  const userTimezone = getUserTimezone();
  return formatInTimeZone(parseISO(isoDatetime), userTimezone, "MMMM d, yyyy · h:mm aa (zzz)");
}

/* Date as "12/07/2024" with timezone parsing */
export function shortUSDateFormat(isoDatetime: string): string {
  if (!isoDatetime) {
    return "";
  }
  const userTimezone = getUserTimezone();
  return formatInTimeZone(parseISO(isoDatetime), userTimezone, "MMM d, yyyy");
}

/* Date as "Jun 10" with timezone parsing */
export function partialUSDateFormat(isoDatetime: string): string {
  if (!isoDatetime) {
    return "";
  }
  const userTimezone = getUserTimezone();
  return formatInTimeZone(parseISO(isoDatetime), userTimezone, "MMM d");
}

/* Time as "3:00 PM" */
export function timeDateFormat(isoDatetime: string): string {
  if (!isoDatetime) {
    return "";
  }
  const userTimezone = getUserTimezone();
  return formatInTimeZone(parseISO(isoDatetime), userTimezone, "h:mm a");
}

/* Parse date string to Date object, handling both ISO and MM/DD/YYYY HH:mm zzz formats */
export function parseDateTimeFormat(dateStr: string | undefined): Date | null {
  if (!dateStr) return null;

  try {
    const directDate = toDate(dateStr);
    if (isValid(directDate)) {
      return directDate;
    }

    const [date, time] = dateStr.split(" ");
    const dateTime = `${date} ${time}`;

    const parsedDate = parse(dateTime, "MM/dd/yyyy HH:mm", new Date());
    return isValid(parsedDate) ? parsedDate : null;
  } catch (error) {
    console.error("Error parsing date:", error);
    return null;
  }
}

/*
 * Format a time string.
 *
 * Formats a time string according to the specified format.
 *
 * @param time The time string in HH:MM:SS format.
 * @param timeFormat The desired time format. Defaults to "h:mm a".
 * @returns The formatted time string.
 */
export function formatTime(time: string, timeFormat: string = "h:mm a"): string {
  if (!time) return "";

  // Parse the time string (HH:MM:SS) into a Date object
  const [hours, minutes, seconds] = time.split(":");
  const date = new Date();
  const parsedSeconds = seconds !== undefined && seconds.trim() !== "" ? parseInt(seconds, 10) : 0;
  date.setHours(parseInt(hours, 10), parseInt(minutes, 10), parsedSeconds);

  // Format using date-fns with 12-hour format and AM/PM
  return format(date, timeFormat);
}

/**
 * Returns the time part of an ISO datetime string, formatted in the runtime's local timezone (HH:mm:ss).
 *
 * Contract: Strings **without** timezone (no Z or ±offset) are **treated as UTC** (we append "Z" before
 * parsing). This matches backend/bot storage (e.g. "2026-02-27T21:07:28" is UTC). Do not assume
 * timezone-less strings are "local" time—that was the previous semantics and caused display bugs.
 */
export function localTimeFromISO(isoDatetime: string): string {
  if (!isoDatetime) return "";
  const hasTimezone = /(Z|[+-]\d{2}(:?\d{2})?(:?\d{2})?)$|([+-]\d{2}:\d{2})$/.test(isoDatetime);
  const toParse = hasTimezone ? isoDatetime : isoDatetime + "Z";
  const date = parseISO(toParse);
  return format(date, "HH:mm:ss");
}

/** Matches HH:mm or HH:mm:ss; used to validate time input before parsing. */
const TIME_STRING_REGEX = /^\d{1,2}:\d{2}(:\d{2})?$/;

/**
 * Converts user's local date + time to UTC date and time strings for storage.
 * The bot stores "${date}T${time}Z"; sending UTC parts ensures the correct instant is stored.
 * When loaded, getLocalDateString/localTimeFromISO convert back to user's timezone for display.
 *
 * @throws Error if timeStr is not HH:mm or HH:mm:ss
 */
export function localDateAndTimeToUTCDateAndTime(
  localDate: Date | string,
  timeStr: string,
): { date: string; time: string } {
  const trimmed = timeStr?.trim() ?? "";
  if (!TIME_STRING_REGEX.test(trimmed)) {
    throw new Error(
      `localDateAndTimeToUTCDateAndTime: timeStr must be HH:mm or HH:mm:ss, got "${timeStr}"`,
    );
  }
  const parts = trimmed.split(":");
  const h = parseInt(parts[0] ?? "0", 10);
  const min = parseInt(parts[1] ?? "0", 10);
  const s = parts[2] !== undefined ? parseInt(parts[2], 10) : 0;
  if (h < 0 || h > 23 || min < 0 || min > 59 || s < 0 || s > 59) {
    throw new Error(`localDateAndTimeToUTCDateAndTime: time out of range (${trimmed})`);
  }
  const date = localDate instanceof Date ? localDate : parseISO(localDate);
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, min, s);
  const y = local.getUTCFullYear();
  const mo = String(local.getUTCMonth() + 1).padStart(2, "0");
  const d = String(local.getUTCDate()).padStart(2, "0");
  const hr = String(local.getUTCHours()).padStart(2, "0");
  const mi = String(local.getUTCMinutes()).padStart(2, "0");
  const sec = String(local.getUTCSeconds()).padStart(2, "0");
  return { date: `${y}-${mo}-${d}`, time: `${hr}:${mi}:${sec}` };
}

/**
 * Builds Observation.effectiveDateTime from UTC calendar date + UTC time-of-day (FHIR valueDate +
 * valueTime) after the client normalized with localDateAndTimeToUTCDateAndTime.
 *
 * @param date - UTC calendar date
 * @param time - UTC time-of-day
 * @returns Observation.effectiveDateTime
 *
 * @example
 * buildEffectiveDateTimeFromUtcParts("2026-03-23", "05:00:00") => "2026-03-23T05:00:00Z"
 * buildEffectiveDateTimeFromUtcParts("2026-03-23", "05:00") => "2026-03-23T05:00:00Z"
 */
export function buildEffectiveDateTimeFromUtcParts(
  date: string | undefined,
  time: string | undefined,
): string | undefined {
  if (!date || !time) return undefined;

  let datePart = date;
  if (datePart.includes("T")) datePart = datePart.slice(0, 10);
  const timePart = time.length >= 8 ? time.slice(0, 8) : `${time.slice(0, 5)}:00`;

  return `${datePart}T${timePart}Z`;
}

/**
 * Inverse of localDateAndTimeToUTCDateAndTime.
 * Given UTC date ("YYYY-MM-DD") and UTC time ("HH:mm" or "HH:mm:ss") as stored in a
 * QuestionnaireResponse, returns a local Date (for a date picker) and a local time string
 * ("HH:mm") for a time input.
 *
 * @param utcDate UTC date string in "YYYY-MM-DD" format
 * @param utcTime UTC time string in "HH:mm" or "HH:mm:ss" format
 */
export function utcDateAndTimeToLocal(
  utcDate: string,
  utcTime: string,
): { date: Date; time: string } {
  const normalizedTime = utcTime.length === 5 ? `${utcTime}:00` : utcTime;
  const isoUtc = `${utcDate}T${normalizedTime}Z`;
  return {
    date: startOfDay(parseISO(isoUtc)),
    time: localTimeFromISO(isoUtc),
  };
}

/**
 * Converts a Date object to a local datetime string in YYYY-MM-DDTHH:mm:ss format
 * This ensures consistent local time handling across the application
 */
export function dateToLocalDateTimeString(date: Date): string {
  return (
    date.getFullYear() +
    "-" +
    String(date.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(date.getDate()).padStart(2, "0") +
    "T" +
    String(date.getHours()).padStart(2, "0") +
    ":" +
    String(date.getMinutes()).padStart(2, "0") +
    ":" +
    String(date.getSeconds()).padStart(2, "0")
  );
}

/**
 * Gets the local date string (YYYY-MM-DD) from a datetime string
 * Handles both UTC and local datetime strings correctly
 */
export function getLocalDateString(datetimeString: string): string {
  if (!datetimeString) return "";

  // If the string doesn't have timezone info, treat it as local time
  // Match Z, +HH, +HHMM, +HH:MM, -HH, -HHMM, -HH:MM at the end of the string
  if (!datetimeString.match(/(Z|[+-]\d{2}(:?\d{2})?(:?\d{2})?)$|([+-]\d{2}:\d{2})$/)) {
    // For strings without timezone info, validate it's a proper date format
    if (datetimeString.length >= 10 && datetimeString.match(/^\d{4}-\d{2}-\d{2}/)) {
      // Check if it's a complete datetime (has time part) or just a date
      if (datetimeString.length === 10 || datetimeString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
        return datetimeString.slice(0, 10);
      }
    }
    return ""; // Invalid format
  }

  // If it has timezone info, convert to local time first
  const userTimezone = getUserTimezone();
  const parsedDate = parseISO(datetimeString);

  // Validate the parsed date
  if (isNaN(parsedDate.getTime())) {
    return "";
  }

  const localDate = formatInTimeZone(parsedDate, userTimezone, "yyyy-MM-dd");
  return localDate;
}

/**
 * Compares two datetime strings, handling both UTC and local datetime formats
 * Returns true if datetime1 >= datetime2
 */
export function compareDateTimeStrings(datetime1: string, datetime2: string): boolean {
  if (!datetime1 || !datetime2) return true;

  // Parse both datetimes using parseISO which handles timezone info correctly
  const date1 = parseISO(datetime1);
  const date2 = parseISO(datetime2);

  // Validate that both dates are valid
  if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
    return true; // If either date is invalid, consider them equal
  }

  return date1 >= date2;
}

/**
 * Combines a local date with a time string and converts to UTC ISO string.
 * @param date The local date (Date object or ISO date string from Mantine DateInput)
 * @param time The time string in HH:mm:ss format (as displayed to user)
 * @returns ISO 8601 datetime string in UTC
 */
export function combineDateTimeToUTC(
  date: Date | string | null,
  time: string | null,
): string | null {
  if (!date || !time) return null;

  const timeParts = time.split(":");
  if (timeParts.length < 2) return null;

  const hours = parseInt(timeParts[0], 10);
  const minutes = parseInt(timeParts[1], 10);
  const seconds = timeParts.length > 2 ? parseInt(timeParts[2], 10) : 0;

  const localDate = date instanceof Date ? date : parseISO(date);
  const localDateTime = set(localDate, { hours, minutes, seconds });

  // Convert to UTC ISO string
  return localDateTime.toISOString();
}

/**
 * Combines a calendar date and wall-clock time in a specific IANA timezone, returning UTC ISO.
 *
 * @param date Calendar date from Mantine DateInput (year/month/day are used as-is).
 * @param time Wall-clock time in HH:mm or HH:mm:ss format.
 * @param timezoneCode IANA timezone identifier (e.g. America/New_York).
 * @returns ISO 8601 datetime string in UTC, or null when inputs are incomplete.
 */
export function combineDateTimeInTimezone(
  date: Date | string | null,
  time: string | null,
  timezoneCode: string | null | undefined,
): string | null {
  if (!date || !time || !timezoneCode) return null;

  const timeParts = time.split(":");
  if (timeParts.length < 2) return null;

  const hours = timeParts[0].padStart(2, "0");
  const minutes = timeParts[1].padStart(2, "0");
  const seconds = (timeParts[2] ?? "0").padStart(2, "0");
  const localDate = date instanceof Date ? date : parseISO(date);
  const datePart = formatDate(localDate, "yyyy-MM-dd");
  const dateTimeStr = `${datePart}T${hours}:${minutes}:${seconds}`;

  return fromZonedTime(dateTimeStr, timezoneCode).toISOString();
}

/**
 * Extracts the local calendar date and time for a UTC instant in a given IANA timezone.
 *
 * @param isoUtc UTC ISO datetime string.
 * @param timezoneCode IANA timezone identifier.
 * @returns Local calendar date and HH:mm time in that timezone.
 */
export function getLocalDateAndTimeInTimezone(
  isoUtc: string,
  timezoneCode: string,
): { date: Date; time: string } {
  const datePart = formatInTimeZone(isoUtc, timezoneCode, "yyyy-MM-dd");
  const timePart = formatInTimeZone(isoUtc, timezoneCode, "HH:mm");

  return {
    date: parseISO(datePart),
    time: timePart,
  };
}

/**
 * Extracts a date-only (`yyyy-MM-dd`) string for a UTC instant in a given IANA timezone, for a FHIR
 * `date` field (e.g. `Patient.birthDate`). Formats directly from the UTC instant rather than going
 * through a `Date` object, so the result never shifts a day depending on the runtime's ambient
 * timezone.
 *
 * Several call sites (e.g. `app/billing/utils/practice-ledger-date-range.ts`,
 * `app/pregnancy/utils/postpartum.ts`) need this exact `yyyy-MM-dd` extraction but only ever need it
 * in UTC. This is the general, timezone-parameterized version for callers (like
 * `createNewbornPatientForBirth`) that need an arbitrary IANA zone, matching
 * `getLocalDateAndTimeInTimezone` above.
 *
 * @param isoUtc UTC ISO datetime string.
 * @param timezoneCode IANA timezone identifier.
 * @returns The local calendar date in that timezone, as `yyyy-MM-dd`.
 */
export function getLocalDateStringInTimezone(isoUtc: string, timezoneCode: string): string {
  return formatInTimeZone(isoUtc, timezoneCode, "yyyy-MM-dd");
}

/**
 * Validates that a form date/time is on or after the birth date/time.
 * Used to prevent postpartum flowsheet entries from being recorded before birth.
 *
 * @param formDate The date from the form (Date object)
 * @param formTime The time from the form (HH:mm:ss string, in local time)
 * @param birthDateTime The birth date/time (ISO 8601 string from FHIR)
 * @returns true if the form date/time is on or after birth, false otherwise
 */
export function isDateTimeOnOrAfterBirth(
  formDate: Date | null | undefined,
  formTime: string | null | undefined,
  birthDateTime: string | null | undefined,
): boolean {
  // If no birth datetime, allow any date (validation not applicable)
  if (!birthDateTime) return true;

  // If form date or time is missing, can't validate
  if (!formDate || !formTime) return true;

  const formDateTime = combineDateTimeToUTC(formDate, formTime);
  if (!formDateTime) return true;

  // Compare: form datetime should be >= birth datetime
  return compareDateTimeStrings(formDateTime, birthDateTime);
}

/**
 * Formats a datetime for display in validation messages.
 *
 * @param birthDateTime The birth date/time (ISO 8601 string)
 * @returns A human-readable date/time string (e.g., "Dec 3, 2025 at 2:30 PM")
 */
export function formatBirthDateTimeForDisplay(birthDateTime: string | null | undefined): string {
  if (!birthDateTime) return "";

  const date = parseISO(birthDateTime);
  if (isNaN(date.getTime())) return "";

  return format(date, "MMM d, yyyy 'at' h:mm a");
}

export function middleOfDay(date: string | number | Date | undefined): Date | undefined {
  if (!date) return undefined;
  // Date-only ISO strings (e.g. "2026-01-17") must be parsed with parseISO so they
  // become local midnight, not UTC midnight. Relying on new Date("2026-01-17")
  // (which date-fns uses internally when given a raw string) shifts the day by one
  // for servers/browsers in UTC- timezones.
  const normalized = typeof date === "string" ? parseISO(date) : date;
  return setHours(startOfDay(normalized), 12);
}
